import api from "./index";
import {
  decorateApiError,
  extractApiErrorMessage,
  isGenerateErrorPayload,
  readFetchError,
} from "../utils/apiErrorUtils";
import {
  getAuthBearerHeaders,
  requireAuthToken,
} from "../utils/authUtils";
import { messageService } from "./message.service";

const GENERATE_STREAM_ENDPOINT = "/generate/stream";

/** @typedef {"openai" | "gemini" | "claude"} GenerateAiName */
/** @typedef {"image" | "video" | "content"} GenerateMediaType */

export const GENERATE_AI_NAMES = {
  OPENAI: "openai",
  GEMINI: "gemini",
  CLAUDE: "claude",
};

export const GENERATE_MEDIA_TYPES = {
  IMAGE: "image",
  VIDEO: "video",
  CONTENT: "content",
};

const defaultMimeForMediaType = (mediaType) => {
  if (mediaType === "video") return "video/mp4";
  if (mediaType === "image") return "image/png";
  return "application/json";
};

const isBinaryMediaBytes = (bytes) => {
  if (!bytes?.length) return false;
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
    return true;
  }
  if (bytes[0] === 0xff && bytes[1] === 0xd8) return true;
  if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) return true;
  if (
    bytes.length > 11 &&
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  ) {
    return true;
  }
  if (
    bytes.length > 7 &&
    bytes[4] === 0x66 &&
    bytes[5] === 0x74 &&
    bytes[6] === 0x79 &&
    bytes[7] === 0x70
  ) {
    return true;
  }
  return false;
};

const detectMimeFromBytes = (bytes, mediaType, contentTypeHeader) => {
  if (bytes[0] === 0x89 && bytes[1] === 0x50) return "image/png";
  if (bytes[0] === 0xff && bytes[1] === 0xd8) return "image/jpeg";
  if (bytes[0] === 0x47 && bytes[1] === 0x49) return "image/gif";
  if (bytes[4] === 0x66 && bytes[5] === 0x74) return "video/mp4";

  const headerMime = contentTypeHeader?.split(";")[0]?.trim();
  if (headerMime && headerMime !== "application/octet-stream") {
    return headerMime;
  }

  return defaultMimeForMediaType(mediaType);
};

const isBinaryContentType = (contentType, mediaType) => {
  const ct = (contentType || "").toLowerCase();
  if (ct.includes("image/") || ct.includes("video/")) return true;
  if (ct.includes("application/octet-stream") && mediaType !== "content") {
    return true;
  }
  return false;
};

const parseSseTextResponse = (text, onChunk, onMetadata, onDelta) => {
  let fullResponse = "";
  let responseFormat = "structured";
  const frames = text.split("\n\n");

  for (const frame of frames) {
    if (!frame.trim()) continue;

    const lines = frame.split("\n");
    const frameDataParts = [];

    for (const line of lines) {
      if (line.startsWith("data:")) {
        frameDataParts.push(line.slice(5));
        continue;
      }
      if (line.startsWith(":")) continue;
      if (frameDataParts.length > 0) frameDataParts.push(line);
    }

    if (!frameDataParts.length) continue;

    const data = frameDataParts.join("\n");
    const trimmed = data.trim();
    if (trimmed === "[DONE]") continue;

    if (trimmed.startsWith("format:")) {
      const formatMatch = trimmed.match(/format:\s*(\w+)/);
      if (formatMatch) {
        responseFormat = formatMatch[1];
        onMetadata?.({ format: responseFormat });
      }
      continue;
    }

    fullResponse += data;
    onDelta?.(data);
    onChunk?.(fullResponse);
  }

  const contentText = fullResponse.trim() || text.trim();
  if (isGenerateErrorPayload(contentText)) {
    const message = extractApiErrorMessage(
      { message: contentText },
      "Generation could not be completed.",
    );
    const error = new Error(message);
    error.isPolicyRejection = true;
    throw error;
  }

  return {
    responseType: "text",
    contentText,
    response: contentText,
    format: responseFormat,
  };
};

const readGenerateResponse = async (
  response,
  mediaType,
  onChunk,
  onMetadata,
  onDelta,
) => {
  const contentType = response.headers.get("content-type") || "";
  const buffer = await response.arrayBuffer();
  const bytes = new Uint8Array(buffer);

  if (
    isBinaryContentType(contentType, mediaType) ||
    (mediaType !== "content" && isBinaryMediaBytes(bytes))
  ) {
    const mimeType = detectMimeFromBytes(bytes, mediaType, contentType);
    const blob = new Blob([buffer], { type: mimeType });
    const mediaUrl = URL.createObjectURL(blob);

    return {
      responseType: "media",
      mediaUrl,
      mediaBlob: blob,
      mimeType,
      contentText: "",
      response: "",
    };
  }

  const text = new TextDecoder().decode(buffer);
  const trimmed = text.trim();

  if (trimmed.includes("data:") || trimmed.includes("\n\n")) {
    return parseSseTextResponse(text, onChunk, onMetadata, onDelta);
  }

  if (isGenerateErrorPayload(trimmed)) {
    const message = extractApiErrorMessage(
      { message: trimmed },
      "Generation could not be completed.",
    );
    const error = new Error(message);
    error.isPolicyRejection = true;
    throw error;
  }

  return {
    responseType: "text",
    contentText: trimmed,
    response: trimmed,
    format: "structured",
  };
};

const isFileLike = (value) =>
  typeof Blob !== "undefined" && value != null && value instanceof Blob;

const appendFormValue = (formData, key, value) => {
  if (value === undefined) return;
  if (isFileLike(value)) {
    const name =
      typeof File !== "undefined" && value instanceof File && value.name
        ? value.name
        : "attachment";
    formData.append(key, value, name);
    return;
  }
  if (value === null) {
    formData.append(key, "");
    return;
  }
  if (typeof value === "boolean") {
    formData.append(key, value ? "true" : "false");
    return;
  }
  formData.append(key, String(value));
};

export const buildGenerateStreamPayload = ({
  silosId,
  conversationId = null,
  modelId = 1,
  projectId = null,
  clientId,
  globallyAvailable = false,
  contentText,
  mediaType,
  aiName,
  attachment = null,
}) => ({
  silosId: Number(silosId),
  conversationId: conversationId != null ? Number(conversationId) : null,
  modelId: Number(modelId ?? 1),
  projectId: projectId != null ? Number(projectId) : null,
  clientId: Number(clientId),
  globallyAvailable: Boolean(globallyAvailable),
  contentText: String(contentText ?? "").trim(),
  mediaType,
  aiName,
  ...(isFileLike(attachment) ? { attachment } : {}),
});

export const toGenerateStreamFormData = (payload = {}) => {
  if (typeof FormData !== "undefined" && payload instanceof FormData) {
    return payload;
  }

  const formData = new FormData();
  const data =
    payload && typeof payload === "object" && !Array.isArray(payload)
      ? payload
      : {};

  appendFormValue(formData, "silosId", data.silosId);
  appendFormValue(formData, "conversationId", data.conversationId ?? "");
  appendFormValue(formData, "modelId", data.modelId);
  appendFormValue(formData, "projectId", data.projectId ?? "");
  appendFormValue(formData, "clientId", data.clientId);
  appendFormValue(formData, "globallyAvailable", data.globallyAvailable);
  appendFormValue(formData, "contentText", data.contentText ?? "");
  appendFormValue(formData, "mediaType", data.mediaType);
  appendFormValue(formData, "aiName", data.aiName);
  if (data.attachment) {
    appendFormValue(formData, "attachment", data.attachment);
  }

  return formData;
};

export const generateStream = async (
  params,
  onChunk = null,
  onMetadata = null,
  onDelta = null,
  options = {},
) => {
  try {
    requireAuthToken();
    const formData = toGenerateStreamFormData(
      buildGenerateStreamPayload(params),
    );
    const headers = getAuthBearerHeaders();
    delete headers["Content-Type"];

    const response = await fetch(
      `${api.defaults.baseURL}${GENERATE_STREAM_ENDPOINT}`,
      {
        method: "POST",
        headers,
        body: formData,
        signal: options?.signal,
      },
    );

    if (!response.ok) {
      throw await readFetchError(
        response,
        response.status === 401
          ? "Authentication required. Please log in again."
          : undefined,
      );
    }

    return readGenerateResponse(
      response,
      params.mediaType,
      onChunk,
      onMetadata,
      onDelta,
    );
  } catch (error) {
    throw decorateApiError(error, "Generation could not be completed.");
  }
};

/** Resolve aiName from generate route path. */
export const getGenerateAiNameFromPath = (pathname = "") => {
  if (pathname.includes("/generate/chatgpt")) {
    return GENERATE_AI_NAMES.OPENAI;
  }
  if (pathname.includes("/generate/gemini")) {
    return GENERATE_AI_NAMES.GEMINI;
  }
  if (pathname.includes("/generate/claude")) {
    return GENERATE_AI_NAMES.CLAUDE;
  }
  return GENERATE_AI_NAMES.GEMINI;
};



const GENERATE_VIEW_ENDPOINT = "/generate/view";

const inferMediaTypeFromFilename = (filename) => {
  const name = String(filename ?? "").toLowerCase();
  if (/\.(mp4|webm|mov)(\?|$)/.test(name)) return "video";
  if (/\.(png|jpe?g|gif|webp)(\?|$)/.test(name)) return "image";
  return null;
};

export const getGenerateView = async ({
  conversationId,
  contentMedia,
  contentMediaBaseUrl,
  mediaType,
}) => {
  try {
    requireAuthToken();

    const resolvedMediaType =
      mediaType ?? inferMediaTypeFromFilename(contentMedia) ?? "image";

    const response = await fetch(
      `${api.defaults.baseURL}${GENERATE_VIEW_ENDPOINT}`,
      {
        method: "POST",
        headers: getAuthBearerHeaders(),
        body: JSON.stringify({
          conversationId: Number(conversationId),
          contentMedia: String(contentMedia ?? ""),
          contentMediaBaseUrl: String(contentMediaBaseUrl ?? ""),
        }),
      },
    );

    if (!response.ok) {
      throw await readFetchError(
        response,
        response.status === 401
          ? "Authentication required. Please log in again."
          : undefined,
      );
    }

    return readGenerateResponse(response, resolvedMediaType);
  } catch (error) {
    throw decorateApiError(error, "Unable to load generated media.");
  }
};

const isImageAttachment = (filename, mimeType = "") => {
  if (String(mimeType).toLowerCase().startsWith("image/")) return true;
  return /\.(png|jpe?g|gif|webp|jfif|bmp|svg)(\?|$)/i.test(String(filename ?? ""));
};

const hydrateUserMessageAttachment = async (msg) => {
  const messageId = msg?.messagesId ?? msg?.messageId ?? msg?.id;
  const attachmentNames = Array.isArray(msg?.attachmentNames)
    ? msg.attachmentNames.map((name) => String(name).trim()).filter(Boolean)
    : String(msg?.attachmentName ?? "").trim()
      ? [String(msg.attachmentName).trim()]
      : [];

  if (
    !messageId ||
    String(messageId).startsWith("msg-") ||
    !attachmentNames.length
  ) {
    return msg;
  }

  const viewed = await Promise.all(
    attachmentNames.map(async (filename) => {
      const viewFilename = String(filename).split(/[/\\]/).pop() || filename;
      try {
        const result = await messageService.viewAttachment(messageId, viewFilename);
        return {
          filename,
          url: result?.url,
          mimeType: result?.mimeType,
        };
      } catch {
        return { filename };
      }
    }),
  );

  const preview = viewed.find(
    (item) => item.url && isImageAttachment(item.filename, item.mimeType),
  );
  const firstUrl = viewed.find((item) => item.url)?.url;
  const blobUrls = viewed
    .map((item) => item.url)
    .filter((url) => String(url ?? "").startsWith("blob:"));

  return {
    ...msg,
    attachmentUrl: firstUrl,
    ...(preview?.url ? { attachmentPreviewUrl: preview.url } : {}),
    ...(blobUrls.length
      ? {
          blobUrlId: `attachment-${messageId}-${attachmentNames[0]}`,
          attachmentBlobUrls: blobUrls,
        }
      : {}),
  };
};

export const hydrateGenerateMessagesMedia = async (conversationId, messages) => {
  if (!conversationId || !Array.isArray(messages) || !messages.length) {
    return messages ?? [];
  }

  return Promise.all(
    messages.map(async (msg) => {
      if (msg?.type === "user") {
        return hydrateUserMessageAttachment(msg);
      }

      const viewParams = msg?.pendingMediaView;
      if (!viewParams?.contentMedia || !viewParams?.contentMediaBaseUrl) {
        return msg;
      }

      try {
        const result = await getGenerateView({
          conversationId,
          contentMedia: viewParams.contentMedia,
          contentMediaBaseUrl: viewParams.contentMediaBaseUrl,
          mediaType: viewParams.mediaType,
        });

        if (result?.responseType === "media" && result.mediaUrl) {
          const { pendingMediaView, loadingMedia, ...rest } = msg;
          return {
            ...rest,
            loadingMedia: false,
            content: result.mediaUrl,
            blobUrlId: result.mediaUrl.startsWith("blob:")
              ? `view-${viewParams.contentMedia}`
              : undefined,
          };
        }
      } catch {
        // Fall through to placeholder content.
      }

      const { pendingMediaView, loadingMedia, ...rest } = msg;
      return {
        ...rest,
        loadingMedia: false,
        isImage: false,
        isVideo: false,
        content: rest.content || "Unable to load media.",
      };
    }),
  );
};